// Helper used by every V_n React variant to read domain-derived content
// (brand name from domain.settings, copy slots from copyPack/copy_overrides,
// photos from spotImages) with the JSX-source literal as the visible fallback.
//
// Usage inside a variant:
//   const { brand, t, ts, spot, isWP, suffix } = useDomainCopy(props);
//   <h1>{ts('hero_headline', 'Solar­anlage planen,')}</h1>
//   <span>{t('nav_brand', brand)}</span>
//   <img src={spot('welcome_hero')} />
//
// In preview mode (window.__previewMode = true) the helper subscribes to a
// postMessage stream of {type:'pv-preview', copyPack, spotImages, service}
// from the admin builder iframe parent. Live edits force a React re-render
// of the variant via the subscriber's useState tick — without this, JSX
// variants would only pick up overrides on full page reload.

import { useState, useEffect } from 'react';

// Module-level overlay populated by the admin builder. Reads merge over
// props.copyPack / props.spotImages so the override layer wins even before
// a save (and saved overrides arrive via props on next reload).
const overlay = {
    copyPack: {},
    spotImages: {},
    service: null,
};

const subscribers = new Set();

function isPreview() {
    return typeof window !== 'undefined' && !!window.__previewMode;
}

function installPreviewListener() {
    if (typeof window === 'undefined') return;
    if (window.__pvDomainCopyListenerInstalled) return;
    window.__pvDomainCopyListenerInstalled = true;
    window.addEventListener('message', (event) => {
        const data = event?.data;
        if (!data || data.type !== 'pv-preview') return;
        if (data.copyPack) Object.assign(overlay.copyPack, data.copyPack);
        if (data.spotImages) Object.assign(overlay.spotImages, data.spotImages);
        if (data.service) overlay.service = data.service;
        subscribers.forEach((cb) => {
            try { cb(); } catch { /* swallow */ }
        });
    });
}

export function useDomainCopy(props = {}) {
    const domain = props.domain ?? null;
    const branding = props.branding ?? null;

    // React state used purely to bump renders when an overlay update arrives.
    const [, setTick] = useState(0);

    useEffect(() => {
        if (!isPreview()) return undefined;
        installPreviewListener();
        const bump = () => setTick((n) => n + 1);
        subscribers.add(bump);
        return () => subscribers.delete(bump);
    }, []);

    const isFilled = (v) => v !== undefined && v !== null && v !== '';

    const service = isPreview() && overlay.service
        ? overlay.service
        : (props.service || 'pv');
    const isWP = service === 'wp';
    const suffix = isWP ? '_wp' : '_pv';

    // Brand label for chrome (header, footer, in-copy mentions). NEVER fall
    // back to `domain.title` — that's the SEO meta title (e.g. "Stromretter24:
    // Echte Bewertungen für Solar & Wärmepumpe") and leaks into "© 2026 <title>
    // GmbH" + body copy as if it were a brand mark. Use settings.brand_name,
    // then branding.brand_name, then a cleaned hostname.
    const hostBrand = (domain && domain.name)
        ? String(domain.name).replace(/\.(de|com|net|org|solar)$/i, '')
        : '';
    const brand =
        (domain && domain.settings && domain.settings.brand_name) ||
        (branding && branding.brand_name) ||
        hostBrand ||
        '';

    // Dynamic month/year labels. Variants previously hardcoded "04/26",
    // "04/2026", "Q1 2026", "Stand 04/2026" — these go stale within weeks.
    // Use `monthLabel` ("04/26"), `monthLabelLong` ("04/2026"), `currentYear`
    // ("2026"), and `currentQuarter` ("Q2 2026") instead of literals.
    const _now = new Date();
    const _mm = String(_now.getMonth() + 1).padStart(2, '0');
    const _yy = String(_now.getFullYear()).slice(-2);
    const _yyyy = String(_now.getFullYear());
    const monthLabel = `${_mm}/${_yy}`;
    const monthLabelLong = `${_mm}/${_yyyy}`;
    const currentYear = _yyyy;
    const currentQuarter = `Q${Math.floor(_now.getMonth() / 3) + 1} ${_yyyy}`;

    const recordEdit = (resolvedKey, rendered) => {
        if (typeof window === 'undefined') return;
        if (!window.__pvFallbacks) window.__pvFallbacks = {};
        if (typeof rendered === 'string' && rendered.length > 0) {
            window.__pvFallbacks[resolvedKey] = rendered;
        }
    };

    const lookup = (key) => {
        if (isPreview()) {
            const live = overlay.copyPack[key];
            if (isFilled(live)) return live;
        }
        return props.copyPack?.[key];
    };

    const t = (key, fallback) => {
        const v = lookup(key);
        const out = isFilled(v) ? v : fallback;
        recordEdit(key, out);
        return out;
    };

    const ts = (baseKey, fallback) => {
        const realKey = baseKey + suffix;
        const v = lookup(realKey);
        const out = isFilled(v) ? v : fallback;
        recordEdit(realKey, out);
        return out;
    };

    const spot = (key) => {
        if (isPreview()) {
            const live = overlay.spotImages[key];
            if (isFilled(live)) return live;
        }
        const v = props.spotImages?.[key];
        return isFilled(v) ? v : null;
    };

    // Responsive variants via SeoController::media() on-the-fly resize.
    // Browser sends Accept: image/webp -> server returns WebP; ?w=N
    // resizes to the requested width (whitelist: 400|640|800|1200|1600).
    // Disk-cached after first hit. Consumer just needs <img srcset>.
    const spotSet = (key) => {
        const base = spot(key);
        if (!base || typeof base !== 'string') return null;
        const sep = base.includes('?') ? '&' : '?';
        return {
            src:  `${base}${sep}w=800`,
            src400:  `${base}${sep}w=400`,
            src800:  `${base}${sep}w=800`,
            src1200: `${base}${sep}w=1200`,
            src1600: `${base}${sep}w=1600`,
            fallback: base,
            srcset: `${base}${sep}w=400 400w, ${base}${sep}w=800 800w, ${base}${sep}w=1200 1200w, ${base}${sep}w=1600 1600w`,
        };
    };

    // Resolve the saved domain logo URL (admin -> Domain -> Logo).
    // Falls back to null when no logo is uploaded; consumers should
    // render their letter-badge fallback in that case.
    const brandLogo = (() => {
        const raw = props.brandLogo
            ?? props.domain?.settings?.logo
            ?? props.branding?.logo
            ?? null;
        if (!raw || typeof raw !== 'string') return null;
        // Admin saves filenames like "heizung-solar-bremen-de.svg" without
        // a leading slash; HomepageController also passes the bare value.
        // Normalise into /media/ paths unless already an absolute URL.
        if (/^https?:\/\//i.test(raw) || raw.startsWith('/')) return raw;
        return '/media/' + raw.replace(/^\/+/, '');
    })();

    return {
        brand, domain, branding, service, isWP, suffix, t, ts, spot, spotSet,
        brandLogo,
        monthLabel, monthLabelLong, currentYear, currentQuarter,
    };
}
